import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  Eye,
  Loader2,
  AlertCircle,
  FileText,
} from "lucide-react";
import { importAPI } from "@/api/import";
import { ImportSession } from "@/types/import";
import { useToast } from "@/hooks/use-toast";

const ImportHistory = forwardRef<{ refreshHistory: () => void }>(
  (props, ref) => {
    const [importSessions, setImportSessions] = useState<ImportSession[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedSession, setSelectedSession] =
      useState<ImportSession | null>(null);
    const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
    const [sessionErrors, setSessionErrors] = useState<any[]>([]);
    const [isLoadingErrors, setIsLoadingErrors] = useState(false);
    const { toast } = useToast();

    useEffect(() => {
      loadImportHistory();
    }, []);

    // Refresh data every 30 seconds to pick up status changes
    useEffect(() => {
      const interval = setInterval(() => {
        loadImportHistory();
      }, 30000);

      return () => clearInterval(interval);
    }, []);

    // Expose refreshHistory function to parent component
    useImperativeHandle(ref, () => ({
      refreshHistory: loadImportHistory,
    }));

    const loadImportHistory = async () => {
      try {
        setIsLoading(true);
        const sessions = await importAPI.getImportSessions();
        setImportSessions(sessions);
      } catch (error) {
        toast({
          title: "Failed to Load Import History",
          description:
            error instanceof Error ? error.message : "Unknown error occurred",
          variant: "destructive",
        });
        // Fall back to mock data if API fails
        setImportSessions([]);
      } finally {
        setIsLoading(false);
      }
    };

    const getStatusBadge = (status: string) => {
      switch (status) {
        case "completed":
          return (
            <Badge
              variant="default"
              className="bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-100"
            >
              <CheckCircle className="w-3 h-3 mr-1" />
              Completed
            </Badge>
          );
        case "failed":
          return (
            <Badge variant="destructive">
              <XCircle className="w-3 h-3 mr-1" />
              Failed
            </Badge>
          );
        default:
          return <Badge variant="outline">{status}</Badge>;
      }
    };

    const formatDate = (dateString: string) => {
      return new Date(dateString).toLocaleString();
    };

    const getSuccessRate = (
      created: number,
      updated: number,
      total: number
    ) => {
      if (total === 0) return 0;
      return Math.round(((created + updated) / total) * 100);
    };

    const handleViewDetails = async (session: ImportSession) => {
      setSelectedSession(session);
      setIsDetailsDialogOpen(true);

      // Load detailed errors if the session has errors
      if (session.errorCount > 0) {
        setIsLoadingErrors(true);
        try {
          const sessionWithErrors = await importAPI.getImportSessionWithErrors(
            session.id
          );
          setSessionErrors(sessionWithErrors.errors);
        } catch (error) {
          console.error("Failed to load error details:", error);
          toast({
            title: "Failed to Load Error Details",
            description: "Could not load detailed error information",
            variant: "destructive",
          });
          setSessionErrors([]);
        } finally {
          setIsLoadingErrors(false);
        }
      } else {
        setSessionErrors([]);
      }
    };

    const handleDownloadReport = (session: ImportSession) => {
      // Generate a simple text report
      const report = generateImportReport(session);

      // Create and download the report
      const blob = new Blob([report], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `import-report-${session.id}-${session.filename}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Report Downloaded",
        description: `Import report for ${session.filename} has been downloaded`,
      });
    };

    const generateImportReport = (session: ImportSession) => {
      const duration = session.completedAt
        ? Math.round(
            (new Date(session.completedAt).getTime() -
              new Date(session.createdAt).getTime()) /
              1000
          )
        : "N/A";

      return `
IMPORT REPORT
=============

File: ${session.filename}
Type: ${session.modelType}
Status: ${session.status}
Started: ${formatDate(session.createdAt)}
Completed: ${session.completedAt ? formatDate(session.completedAt) : "N/A"}
Duration: ${duration}s

RESULTS
-------
Total Rows Processed: ${session.totalRows}
Successfully Created: ${session.createdCount}
Successfully Updated: ${session.updatedCount}
Errors: ${session.errorCount}
Success Rate: ${getSuccessRate(
        session.createdCount,
        session.updatedCount,
        session.totalRows
      )}%

PROGRESS
--------
Processed Rows: ${session.processedRows}

${
  session.errorCount > 0
    ? `
NOTES
-----
This import had ${session.errorCount} error(s). For detailed error information, 
please check the Import History details view in the application.
`
    : ""
}

Generated on: ${new Date().toLocaleString()}
    `.trim();
    };

    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Import History
            </CardTitle>
            <CardDescription>
              View and manage your previous data import sessions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 mx-auto animate-spin mb-4" />
                <p className="text-muted-foreground">
                  Loading import history...
                </p>
              </div>
            ) : importSessions.length === 0 ? (
              <div className="text-center py-8">
                <FileSpreadsheet className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No imports yet</h3>
                <p className="text-muted-foreground">
                  Your import history will appear here once you start importing
                  data.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">
                        Total Imports
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {importSessions.length}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">
                        Successful
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-green-500 dark:text-green-400">
                        {
                          importSessions.filter((i) => i.status === "completed")
                            .length
                        }
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">
                        Failed
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-red-500 dark:text-red-400">
                        {
                          importSessions.filter((i) => i.status === "failed")
                            .length
                        }
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>File & Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Results</TableHead>
                        <TableHead>Success Rate</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importSessions.map((importSession) => (
                        <TableRow key={importSession.id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">
                                {importSession.filename}
                              </div>
                              <div className="text-sm text-muted-foreground capitalize">
                                {importSession.modelType}
                              </div>
                            </div>
                          </TableCell>

                          <TableCell>
                            {getStatusBadge(importSession.status)}
                          </TableCell>

                          <TableCell>
                            <div className="text-sm space-y-1">
                              <div>Total: {importSession.totalRows}</div>
                              <div className="flex gap-3">
                                <span className="text-green-500 dark:text-green-400">
                                  Created: {importSession.createdCount}
                                </span>
                                <span className="text-blue-500 dark:text-blue-400">
                                  Updated: {importSession.updatedCount}
                                </span>
                                {importSession.errorCount > 0 && (
                                  <span className="text-red-500 dark:text-red-400">
                                    Errors: {importSession.errorCount}
                                  </span>
                                )}
                              </div>
                            </div>
                          </TableCell>

                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="text-sm font-medium">
                                {getSuccessRate(
                                  importSession.createdCount,
                                  importSession.updatedCount,
                                  importSession.totalRows
                                )}
                                %
                              </div>
                            </div>
                          </TableCell>

                          <TableCell>
                            <div className="text-sm">
                              <div>{formatDate(importSession.createdAt)}</div>
                              {importSession.completedAt && (
                                <div className="text-muted-foreground">
                                  Duration:{" "}
                                  {Math.round(
                                    (new Date(
                                      importSession.completedAt
                                    ).getTime() -
                                      new Date(
                                        importSession.createdAt
                                      ).getTime()) /
                                      1000
                                  )}
                                  s
                                </div>
                              )}
                            </div>
                          </TableCell>

                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewDetails(importSession)}
                                title="View Details"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  handleDownloadReport(importSession)
                                }
                                title="Download Report"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Details Dialog */}
        <Dialog
          open={isDetailsDialogOpen}
          onOpenChange={setIsDetailsDialogOpen}
        >
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Import Details
              </DialogTitle>
              <DialogDescription>
                Detailed information about the import session
              </DialogDescription>
            </DialogHeader>

            {selectedSession && (
              <div className="space-y-6">
                {/* Session Info */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">
                      Session Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid gap-3 md:grid-cols-2">
                      <div>
                        <span className="text-sm font-medium text-muted-foreground">
                          File:
                        </span>
                        <div className="font-medium">
                          {selectedSession.filename}
                        </div>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-muted-foreground">
                          Type:
                        </span>
                        <div className="font-medium capitalize">
                          {selectedSession.modelType}
                        </div>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-muted-foreground">
                          Status:
                        </span>
                        <div>{getStatusBadge(selectedSession.status)}</div>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-muted-foreground">
                          Started:
                        </span>
                        <div className="font-medium">
                          {formatDate(selectedSession.createdAt)}
                        </div>
                      </div>
                    </div>

                    {selectedSession.completedAt && (
                      <div className="grid gap-3 md:grid-cols-2">
                        <div>
                          <span className="text-sm font-medium text-muted-foreground">
                            Completed:
                          </span>
                          <div className="font-medium">
                            {formatDate(selectedSession.completedAt)}
                          </div>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-muted-foreground">
                            Duration:
                          </span>
                          <div className="font-medium">
                            {selectedSession.duration
                              ? `${(selectedSession.duration / 1000).toFixed(
                                  1
                                )}s`
                              : `${Math.round(
                                  (new Date(
                                    selectedSession.completedAt
                                  ).getTime() -
                                    new Date(
                                      selectedSession.createdAt
                                    ).getTime()) /
                                    1000
                                )}s`}
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Results Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Results Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                      <div className="text-center p-3 border rounded-lg">
                        <div className="text-2xl font-bold">
                          {selectedSession.totalRows}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Total Rows
                        </div>
                      </div>
                      <div className="text-center p-3 border rounded-lg">
                        <div className="text-2xl font-bold text-green-500">
                          {selectedSession.createdCount}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Created
                        </div>
                      </div>
                      <div className="text-center p-3 border rounded-lg">
                        <div className="text-2xl font-bold text-blue-500">
                          {selectedSession.updatedCount}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Updated
                        </div>
                      </div>
                      <div className="text-center p-3 border rounded-lg">
                        <div className="text-2xl font-bold text-red-500">
                          {selectedSession.errorCount}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Errors
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 p-3 bg-muted rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Success Rate:</span>
                        <span className="text-lg font-bold">
                          {getSuccessRate(
                            selectedSession.createdCount,
                            selectedSession.updatedCount,
                            selectedSession.totalRows
                          )}
                          %
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Error Details */}
                {selectedSession.errorCount > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-red-500" />
                        Error Details ({selectedSession.errorCount} errors)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {isLoadingErrors ? (
                        <div className="flex items-center justify-center p-4">
                          <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
                          <span className="ml-2">Loading error details...</span>
                        </div>
                      ) : sessionErrors.length > 0 ? (
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {sessionErrors.map((error, index) => (
                            <div
                              key={error.id || index}
                              className="p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg"
                            >
                              <div className="flex items-start justify-between mb-2">
                                <span className="text-sm font-medium text-red-800 dark:text-red-200">
                                  Row {error.row_number}
                                </span>
                                <span className="text-xs text-red-600 dark:text-red-400 capitalize">
                                  {error.error_type}
                                </span>
                              </div>
                              <p className="text-sm text-red-700 dark:text-red-300 mb-2">
                                {error.error_message}
                              </p>
                              {error.row_data &&
                                Object.keys(error.row_data).length > 0 && (
                                  <details className="text-xs">
                                    <summary className="cursor-pointer text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200">
                                      Show row data
                                    </summary>
                                    <div className="mt-2 p-2 bg-red-100 dark:bg-red-900/30 rounded border">
                                      <pre className="whitespace-pre-wrap text-red-800 dark:text-red-200">
                                        {JSON.stringify(
                                          error.row_data,
                                          null,
                                          2
                                        )}
                                      </pre>
                                    </div>
                                  </details>
                                )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg">
                          <p className="text-sm text-red-800 dark:text-red-200">
                            This import session encountered{" "}
                            {selectedSession.errorCount} error(s) during
                            processing, but detailed error information is not
                            available.
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    onClick={() => handleDownloadReport(selectedSession)}
                    className="flex-1"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Full Report
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setIsDetailsDialogOpen(false)}
                    className="flex-1"
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }
);

ImportHistory.displayName = "ImportHistory";

export default ImportHistory;
